"use client";

import { c as _c } from "react/compiler-runtime";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { faCompress } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FocusTrap } from "focus-trap-react";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import { useEffect, useId, useRef, type MouseEvent } from "react";
import { SingleWaveDropPosition } from "./SingleWaveDropPosition";
import { SingleWaveDropVotes } from "./SingleWaveDropVotes";
import { isMemesDropImageMedia, type MemesDropMedia } from "./memesDropPanelTypes";
interface MemesDropFullscreenOverlayProps {
  readonly isOpen: boolean;
  readonly artworkMedia?: MemesDropMedia | null | undefined;
  readonly drop: ExtendedDrop;
  readonly title: string;
  readonly description: string;
  readonly onClose: () => void;
}
export function MemesDropFullscreenOverlay(t0) {
  const $ = _c(25);
  const {
    isOpen,
    artworkMedia,
    drop,
    title,
    description,
    onClose
  } = t0;
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();
  let t1;
  if ($[0] !== artworkMedia || $[1] !== isOpen) {
    t1 = isOpen && isMemesDropImageMedia(artworkMedia);
    $[0] = artworkMedia;
    $[1] = isOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isOverlayVisible = t1;
  let t2;
  let t3;
  if ($[3] !== isOverlayVisible || $[4] !== onClose) {
    t2 = () => {
      if (!isOverlayVisible) {
        return;
      }
      const handleKeyDown = event => {
        if (event.key !== "Escape") {
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        onClose();
      };
      document.addEventListener("keydown", handleKeyDown, true);
      return () => {
        document.removeEventListener("keydown", handleKeyDown, true);
      };
    };
    t3 = [isOverlayVisible, onClose];
    $[3] = isOverlayVisible;
    $[4] = onClose;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== onClose) {
    t4 = event_0 => {
      if (event_0.target !== event_0.currentTarget) {
        return;
      }
      onClose();
    };
    $[7] = onClose;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleBackdropClick = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      allowOutsideClick: true,
      escapeDeactivates: false,
      fallbackFocus: () => dialogRef.current ?? document.body,
      initialFocus: () => closeButtonRef.current ?? dialogRef.current ?? document.body,
      returnFocusOnDeactivate: true
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== artworkMedia || $[11] !== description || $[12] !== descriptionId || $[13] !== drop || $[14] !== handleBackdropClick || $[15] !== isOverlayVisible || $[16] !== onClose || $[17] !== title || $[18] !== titleId) {
    t6 = isOverlayVisible && <m.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-iron-950/90 tw-p-4" onClick={handleBackdropClick}><dialog ref={dialogRef} open={true} aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined} aria-modal="true" tabIndex={-1} className="tw-static tw-m-0 tw-flex tw-h-full tw-max-h-full tw-w-full tw-max-w-5xl tw-flex-col tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-iron-100"><div className="tw-mb-4 tw-flex tw-w-full tw-items-center tw-justify-between"><div className="tw-flex tw-flex-col"><div className="tw-flex tw-items-center tw-gap-x-3"><SingleWaveDropPosition rank={drop.rank} drop={drop} /><h3 id={titleId} className="tw-text-xl tw-font-semibold tw-text-iron-100">{title}</h3></div>{description && <p id={descriptionId} className="tw-ml-10 tw-mt-1 tw-text-md tw-text-iron-400">{description}</p>}</div><div className="tw-mx-auto"><SingleWaveDropVotes drop={drop} /></div><button ref={closeButtonRef} type="button" onClick={onClose} className="tw-rounded-lg tw-bg-iron-900/80 tw-p-3 tw-text-iron-100 tw-transition-colors tw-duration-200 hover:tw-bg-iron-800 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400" aria-label="Exit fullscreen view"><FontAwesomeIcon icon={faCompress} className="tw-h-5 tw-w-5" /></button></div><div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center"><img src={artworkMedia.url} alt={title} className="tw-max-h-full tw-max-w-full tw-object-contain" /></div></dialog></m.div>;
    $[10] = artworkMedia;
    $[11] = description;
    $[12] = descriptionId;
    $[13] = drop;
    $[14] = handleBackdropClick;
    $[15] = isOverlayVisible;
    $[16] = onClose;
    $[17] = title;
    $[18] = titleId;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t6) {
    t7 = <div><LazyMotion features={domAnimation}><AnimatePresence>{t6}</AnimatePresence></LazyMotion></div>;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== isOverlayVisible || $[23] !== t7) {
    t8 = <FocusTrap active={isOverlayVisible} focusTrapOptions={t5}>{t7}</FocusTrap>;
    $[22] = isOverlayVisible;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}